/**
 * @fileoverview The organization's own time zone (PUT /org/timezone).
 *
 * Every class meets in its program address's time zone, and in this one when
 * the program has no address (services/schedule_zones), so a whole
 * organization can run on one zone without an address on every program. The
 * dashboard's "today" and the program emails read it too. Classes already set
 * up in another zone are listed under it, to move.
 */

import { useContext, useState } from "react";
import { toast } from "sonner";

import { OrgAuthContext } from "@/App";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { Button, Card, SectionHeader } from "@/components/studio/primitives";
import TimezoneSelect from "@/components/TimezoneSelect";
import ScheduleZoneCheck from "@/components/org/ScheduleZoneCheck";

const selectClass =
  "w-full max-w-sm rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 py-[10px] font-sans text-[15px] text-[var(--text-primary)] outline-none focus:border-[var(--accent-yellow)]";

export default function OrgTimezoneCard() {
  const { org, refresh } = useContext(OrgAuthContext) || {};
  const saved = org?.timezone || "";
  const [zone, setZone] = useState(saved);
  const [saving, setSaving] = useState(false);
  const [revision, setRevision] = useState(0);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await orgApi.put("/org/timezone", { timezone: zone });
      toast.success("Time zone saved");
      await refresh?.();
      setRevision((n) => n + 1);
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't save the time zone"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <form onSubmit={save} data-testid="org-timezone">
        <Card>
          <SectionHeader>Time zone</SectionHeader>
          <p className="mb-4 font-sans text-[13px] text-[var(--text-tertiary)]">
            Class times are read in this time zone, unless a program has an address with a time zone of its own.
            {!saved && " None is set yet, so classes take the time zone of the computer that sets them up."}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <TimezoneSelect
              value={zone}
              onChange={(e) => setZone(e.target.value)}
              aria-label="Organization time zone"
              data-testid="org-timezone-select"
              className={selectClass}
            />
            <Button type="submit" disabled={saving || !zone || zone === saved} data-testid="org-timezone-save">
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </Card>
      </form>
      <ScheduleZoneCheck revision={revision} testId="org-zone-check" />
    </>
  );
}
